import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import "./index.css";

import { AuthProvider, useAuth, useMayUse } from "@/app/auth";
import { AppLayout } from "@/app/layout";
import { SignInPage } from "@/features/signin";
import { MfaGatePage, SecurityPage } from "@/features/mfa";
import { AnnouncementsPage } from "@/features/announcements";
import { DashboardPage, JobsPage } from "@/features/pages";
import { CompaniesPage, CompanyDetailPage } from "@/features/companies";
import { UserDetailPage, UsersPage } from "@/features/users";
import { DefinitionsPage } from "@/features/definitions";
import { BillingPage } from "@/features/billing";
import { HybridCallPage, HybridPage } from "@/features/hybrid";
import {
  AlertsPage,
  LogsPage,
  RobotPage,
  SyncPage,
  SystemPage,
} from "@/features/operations";
import { ApiError, onUnauthenticated, type Area } from "@/lib/api";
import { Spinner } from "@/components/ui";

// The statistics carry a charting library the other pages do not need, so
// they are fetched when somebody opens them.
const StatsPage = lazy(() => import("@/features/stats"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Losing the session is an ordinary outcome, not something to retry;
      // so is owing a second factor, which no number of retries will supply.
      retry: (count, error) =>
        !(
          error instanceof ApiError &&
          (error.isUnauthenticated || error.isMfaRequired)
        ) && count < 2,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

// Ein Tab, der schon offen war, als die Sitzung ihren zweiten Faktor schuldig
// wurde, bekommt die Absage an irgendeinem Endpunkt — nicht beim Anmelden.
// Statt dort eine Fehlermeldung stehen zu lassen, holen wir uns unseren
// eigenen Zustand neu; der Router schickt uns dann selbst zur Schranke.
//
// Dasselbe gilt für einen Bereich, den die Rolle nicht mehr betreten darf:
// auch das heißt, dass unser Bild von uns selbst veraltet ist.
//
// Eine abgelaufene Sitzung (401) heißt: wir sind nicht mehr angemeldet. Das
// zwischengespeicherte "me" würde das sonst noch eine halbe Minute lang
// bestreiten — und ein fehlgeschlagener Neuabruf lässt die alten Daten stehen —,
// also werfen wir es weg, samt allem, was der alten Sitzung gehörte;
// RequireAuth schickt dann zur Anmeldung. Das gilt für jede Anfrage, auch für
// die, die eine Seite am Cache vorbei stellt, deshalb hängt es in api() selbst.
// Ausgenommen sind die Endpunkte der Anmeldung: dort heißt 401 "Passwort oder
// Passkey falsch", und die Seite sagt das selbst.
function isSignInEndpoint(path: string): boolean {
  const p = path.split("?")[0]!;
  return (
    p === "/api/admin/session" ||
    p.startsWith("/api/admin/passkeys/login") ||
    p.startsWith("/api/admin/mfa/passkey")
  );
}

let signingOut = false;
function sessionLost(path: string) {
  if (isSignInEndpoint(path)) return;
  if (signingOut) return;
  const me = queryClient.getQueryData(["me"]);
  if (!me) return;
  signingOut = true;
  // Erst nach der Anfrage, die uns gerade aufruft, nicht mitten in ihr.
  queueMicrotask(() => {
    void queryClient.cancelQueries();
    // Alles andere gehörte der alten Sitzung.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    queryClient.getMutationCache().clear();
    queryClient.setQueryData(["me"], null);
    signingOut = false;
  });
}

for (const cache of [queryClient.getQueryCache(), queryClient.getMutationCache()]) {
  cache.subscribe((event) => {
    const error = (event as { error?: unknown; action?: { error?: unknown } })
      .action?.error;
    if (
      error instanceof ApiError &&
      (error.isMfaRequired || error.isAreaForbidden)
    ) {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    }
  });
}
onUnauthenticated(sessionLost);

/**
 * Sends anybody without a session to the sign-in page, and anybody whose
 * session still owes its second factor to the screen that settles it.
 *
 * The second redirect is the interface's half of the rule the server enforces:
 * the server refuses every endpoint behind the factor, and this makes that
 * refusal a screen rather than a page full of error notes.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useAuth();
  if (loading) return <Spinner label="Anmeldung wird geprüft" />;
  if (!me) return <Navigate to="/signin" replace />;
  if (me.mfaPending) return <Navigate to="/mfa" replace />;
  return <>{children}</>;
}

/**
 * Hält die Bereiche geschlossen, die dem Betreiber gehören und nicht einer
 * Firma: wer sie nicht betreten darf, landet auf der Übersicht statt auf einer
 * Seite voller Absagen.
 *
 * Das Menü blendet dieselben Einträge aus; diese Schranke ist für den Weg
 * daran vorbei — eine eingetippte Adresse, ein alter Bookmark.
 */
function RequireArea({ area, children }: { area: Area; children: React.ReactNode }) {
  const allowed = useMayUse(area);
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/mfa" element={<MfaGatePage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:id" element={<CompanyDetailPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route
          path="definitions"
          element={
            <RequireArea area="definitions">
              <DefinitionsPage />
            </RequireArea>
          }
        />
        <Route path="jobs" element={<JobsPage />} />
        <Route
          path="billing"
          element={
            <RequireArea area="billing">
              <BillingPage />
            </RequireArea>
          }
        />
        <Route
          path="sync"
          element={
            <RequireArea area="sync">
              <SyncPage />
            </RequireArea>
          }
        />
        <Route
          path="stats"
          element={
            <Suspense fallback={<Spinner />}>
              <StatsPage />
            </Suspense>
          }
        />
        <Route path="logs" element={<LogsPage />} />
        <Route
          path="alerts"
          element={
            <RequireArea area="alerts">
              <AlertsPage />
            </RequireArea>
          }
        />
        <Route
          path="robot"
          element={
            <RequireArea area="robot">
              <RobotPage />
            </RequireArea>
          }
        />
        <Route path="robot/explore" element={<Navigate to="/robot?tab=explore" replace />} />
        <Route
          path="hybrid"
          element={
            <RequireArea area="robot">
              <HybridPage />
            </RequireArea>
          }
        />
        <Route
          path="hybrid/calls/:id"
          element={
            <RequireArea area="robot">
              <HybridCallPage />
            </RequireArea>
          }
        />
        <Route
          path="system"
          element={
            <RequireArea area="system">
              <SystemPage />
            </RequireArea>
          }
        />
        <Route path="security" element={<SecurityPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
